import React, {useState, useEffect} from 'react';
import LoadingSpinner from './LoadingSpinner';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import i18n from 'i18n';

interface Conversation {
    id: number;
    hash: string;
    title?: string;
    updatedAt: string;
    messages?: Array<{
        id: number;
        role: string;
        content: string;
        createdAt: string;
    }>;
}

interface PaginationData {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
    next_num: number | null;
    prev_num: number | null;
}

interface ChatHistoryProps {
    history: Conversation[];
    loading: boolean;
    onNavigate: (view: 'chat', data?: {conversation?: Conversation, clearMessages?: boolean}) => void;
    pagination: PaginationData | null;
    currentPage: number;
    onPageChange: (page: number) => void;
    searchTerm: string;
    onSearchChange: (search: string) => void;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
    history, 
    loading, 
    onNavigate, 
    pagination, 
    currentPage, 
    onPageChange, 
    searchTerm, 
    onSearchChange
}) => {
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

    useEffect(() => {
        setLocalSearchTerm(searchTerm);
    }, [searchTerm]);

    const handleConversationSelect = (conversation: Conversation) => {
        onNavigate('chat', {conversation});
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearchChange(localSearchTerm);
    };

    const handleSearchClear = () => {
        setLocalSearchTerm('');
        onSearchChange('');
    };

    // Generate page numbers for pagination
    const getPageNumbers = (): number[] => {
        if (!pagination) {return [];}
        
        const totalPages = pagination.pages;
        const current = currentPage;
        
        if (totalPages <= 7) {
            // Show all pages if 7 or fewer
            return Array.from({length: totalPages}, (_, i) => i + 1);
        }
        
        const pages: number[] = [];
        
        // Always show first page
        pages.push(1);
        
        if (current <= 4) {
            // Current page is near the beginning
            for (let i = 2; i <= 5; i++) {
                pages.push(i);
            }
            if (totalPages > 6) {
                pages.push(-1); // Ellipsis
            }
            pages.push(totalPages);
        } else if (current >= totalPages - 3) {
            // Current page is near the end
            if (totalPages > 6) {
                pages.push(-1); // Ellipsis
            }
            for (let i = totalPages - 4; i <= totalPages; i++) {
                if (i > 1) {
                    pages.push(i);
                }
            }
        } else {
            // Current page is in the middle
            pages.push(-1); // Ellipsis
            for (let i = current - 1; i <= current + 1; i++) {
                pages.push(i);
            }
            pages.push(-1); // Ellipsis
            pages.push(totalPages);
        }
        
        return pages;
    };


    return (
        <>
            {/* Search Bar */}
            <div className="cb-search-container" style={{padding: '10px', borderBottom: '1px solid #eee'}}>
                <form onSubmit={handleSearchSubmit} style={{display: 'flex', gap: '5px'}}>
                    <input
                        type="text"
                        value={localSearchTerm}
                        onChange={(e) => setLocalSearchTerm(e.target.value)}
                        placeholder={i18n.t('jt_search_conversations.text') || 'Search conversations...'}
                        className="form-control"
                        style={{fontSize: '14px'}}
                    />
                    <button 
                        type="submit" 
                        className="btn btn-sm btn-primary"
                        disabled={loading}
                    >
                        🔍
                    </button>
                    {searchTerm && (
                        <button 
                            type="button" 
                            className="btn btn-sm btn-secondary"
                            onClick={handleSearchClear}
                            disabled={loading}
                        >
                            ✕
                        </button>
                    )}
                </form>
            </div>

            <div className="cb-chat-box">
                <div>
                    {loading ? (
                        <LoadingSpinner />
                    ) : history.length > 0 ? (
                        history.map((conversation: Conversation) => (
                            <div
                                key={conversation.id}
                                className="cb-history-item cb-history-item-clickable"
                                onClick={() => handleConversationSelect(conversation)}
                            >
                                <div className="cb-history-text">
                                    <span className="cb-history-title-primary">
                                        Conversation {conversation.id}
                                    </span>
                                    <span className="cb-history-title-secondary">
                                        {conversation.title || 'Untitled Conversation'}
                                    </span>
                                </div>

                                <div className="cb-history-time">
                                    {new Date(conversation.updatedAt).toLocaleDateString()}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="cb-history-no-conversations">
                            {searchTerm ? 
                                `No conversations found for "${searchTerm}"` : 
                                i18n.t('jt_no_conversation_history_yet.text')
                            }
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                    <div style={{padding: '10px', borderTop: '1px solid #eee'}}>
                        <PaginationComponent
                            pages={getPageNumbers()}
                            currentPage={currentPage}
                            onPageChange={onPageChange}
                            className="mb-0"
                            centered={true}
                        />
                        <div style={{textAlign: 'center', fontSize: '12px', color: '#666', marginTop: '5px'}}>
                            Showing {((currentPage - 1) * pagination.per_page) + 1} to {Math.min(currentPage * pagination.per_page, pagination.total)} of {pagination.total} conversations
                        </div>
                    </div>
                )}
            </div>
            <div className="cb-history-footer">
                <div className="cb-history-footer-container">
                    <div className="cb-new-chat-wrapper" onClick={() => onNavigate('chat', {clearMessages: true})}>
                        <button className="cb-new-chat-btn" data-cy="chat-history-btn">
                            {i18n.t('bt_start_new_conversation.text')}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChatHistory;
